/**
 * <nutrition-facts> — sections/product-nutrition.liquid.
 *
 * On a gift set the section holds one panel per oil (cards + table), behind a row of chips
 * ([data-nutrition-tab] buttons, aria-pressed). Only the pressed chip's panel is shown; its figures
 * count up when it appears. Without this script the first panel stays shown and the chips do nothing.
 *
 * Two bits of motion, both optional: the section is fully usable with this script blocked.
 *  - The <details> table opens and closes on an animated height instead of snapping.
 *  - The four highlight figures count up from zero the first time they scroll into view. Every
 *    figure runs for the same duration, so 0 and 900 land together rather than at their own
 *    speeds.
 *
 * With `prefers-reduced-motion: reduce` neither runs: the table toggles natively and the
 * figures stay at their final value, which is what the server already rendered.
 */
const COUNT_MS = 900;
/* Keep in step with --pn-toggle-duration / --pn-ease in the section's stylesheet. */
const TOGGLE_MS = 420;
/*
  A soft start matters more than a soft landing here: the brand's usual cubic-bezier(0.22, 1,
  0.36, 1) covers most of the height in its first third, which reads as the table snapping open
  and then easing. This curve accelerates gently instead.
*/
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';

/** easeOutCubic — fast start, soft landing, same shape as the CSS easing above. */
const easeOut = (t) => 1 - (1 - t) ** 3;

/**
 * Splits "52g" into the parts needed to rebuild it mid-count: leading text, the number, and
 * trailing text. Keeps the decimal separator the merchant typed ("4,7" stays comma-decimal).
 * @param {string} text
 */
export function parseFigure(text) {
  const match = text.match(/^(\D*?)(\d+(?:[.,]\d+)?)(.*)$/s);
  if (!match) return null;

  const [, prefix, digits, suffix] = match;
  const separator = digits.includes(',') ? ',' : '.';
  const [, fraction = ''] = digits.split(/[.,]/);

  return {
    prefix,
    suffix,
    separator,
    decimals: fraction.length,
    target: Number.parseFloat(digits.replace(',', '.')),
  };
}

/** @param {ReturnType<typeof parseFigure>} figure @param {number} value */
export const format = (figure, value) =>
  figure.prefix + value.toFixed(figure.decimals).replace('.', figure.separator) + figure.suffix;

class NutritionFacts extends HTMLElement {
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  /** Per table: the running height animation and whether it is closing. */
  /** @type {WeakMap<HTMLDetailsElement, { animation: Animation | null, closing: boolean }>} */
  #toggles = new WeakMap();
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #counted = false;

  connectedCallback() {
    for (const details of this.querySelectorAll('details')) {
      if (!details.querySelector('[data-nutrition-panel]')) continue;
      details.querySelector('summary')?.addEventListener('click', this.#onSummaryClick);
    }

    for (const tab of this.querySelectorAll('[data-nutrition-tab]')) {
      tab.addEventListener('click', this.#onTabClick);
    }

    this.#watchForCountUp();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  /* ---------------- Set tabs ---------------- */

  /** @param {MouseEvent} event */
  #onTabClick = (event) => {
    const tab = /** @type {HTMLElement} */ (event.currentTarget);
    if (tab.getAttribute('aria-pressed') === 'true') return;

    for (const other of this.querySelectorAll('[data-nutrition-tab]')) {
      other.setAttribute('aria-pressed', String(other === tab));
    }

    let shown = null;
    for (const panel of this.querySelectorAll('[data-nutrition-set-panel]')) {
      const match = panel.getAttribute('data-nutrition-set-panel') === tab.dataset.nutritionTab;
      panel.hidden = !match;
      if (match) shown = panel;
    }

    // A panel shown after the first count-up counts on its own; before it, the observer will.
    if (shown && this.#counted && !this.#reducedMotion.matches) {
      this.#countUp(/** @type {HTMLElement[]} */ ([...shown.querySelectorAll('[data-figure]')]));
    }
  };

  /* ---------------- Disclosure ---------------- */

  /** @param {MouseEvent} event */
  #onSummaryClick = (event) => {
    const details = /** @type {HTMLDetailsElement | null} */ (
      /** @type {HTMLElement} */ (event.currentTarget).closest('details')
    );
    const panel = /** @type {HTMLElement | null} */ (details?.querySelector('[data-nutrition-panel]') ?? null);
    if (!details || !panel || this.#reducedMotion.matches) return;

    let state = this.#toggles.get(details);
    if (!state) {
      state = { animation: null, closing: false };
      this.#toggles.set(details, state);
    }

    // Take the toggle over from the browser so the height can be animated in both directions.
    event.preventDefault();

    // Direction comes from `open`, never from a measurement: a closed <details> still reports
    // the height its panel had when it was last laid out. While closing, `open` is still true
    // (it is cleared when the animation finishes), so state.closing is what says which way to go.
    const opening = !details.open || state.closing;
    // Measure before cancelling, so a click mid-animation resumes from where it got to.
    const from = details.open ? panel.getBoundingClientRect().height : 0;

    state.animation?.cancel();
    state.closing = !opening;
    if (opening) details.open = true;

    // The glyph is driven by CSS. `open` stays set for the whole closing animation, so this
    // attribute is what tells the plus to come back now rather than at the end.
    details.toggleAttribute('data-closing', state.closing);

    const to = opening ? panel.scrollHeight : 0;

    panel.style.overflow = 'hidden';
    // The rows fade with the height, so the table does not arrive at full strength while the
    // panel is still a sliver.
    const animation = panel.animate(
      [
        { height: `${from}px`, opacity: opening ? 0 : 1 },
        { height: `${to}px`, opacity: opening ? 1 : 0 },
      ],
      { duration: TOGGLE_MS, easing: EASE }
    );

    state.animation = animation;

    animation.addEventListener('finish', () => {
      panel.style.overflow = '';
      state.animation = null;
      if (state.closing) {
        details.open = false;
        state.closing = false;
        details.removeAttribute('data-closing');
      }
    });
  };

  /* ---------------- Counting figures ---------------- */

  #watchForCountUp() {
    // Hidden set panels count when they are shown (#onTabClick)
    const figures = /** @type {HTMLElement[]} */ (
      [...this.querySelectorAll('[data-figure]')].filter((figure) => !figure.closest('[hidden]'))
    );
    if (figures.length === 0 || this.#reducedMotion.matches) return;

    if (!('IntersectionObserver' in window)) {
      this.#countUp(figures);
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (this.#counted || !entries.some((entry) => entry.isIntersecting)) return;
        this.#counted = true;
        this.#observer?.disconnect();
        this.#countUp(figures);
      },
      { threshold: 0.4 }
    );
    this.#observer.observe(this);
  }

  /** @param {HTMLElement[]} elements */
  #countUp(elements) {
    const figures = elements
      .map((element) => ({ element, figure: parseFigure(element.textContent ?? '') }))
      .filter((entry) => entry.figure !== null);
    if (figures.length === 0) return;

    // The figures are centred block elements, so digits appearing never shifts the layout;
    // tabular-nums in the CSS keeps them from dancing as the digits change.
    for (const { element, figure } of figures) element.textContent = format(figure, 0);

    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / COUNT_MS, 1);
      const eased = easeOut(progress);
      for (const { element, figure } of figures) {
        element.textContent = format(figure, figure.target * eased);
      }
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
}

if (!customElements.get('nutrition-facts')) {
  customElements.define('nutrition-facts', NutritionFacts);
}
